/**
 * Persistent cache using IndexedDB with fallback to memory
 *
 * Provides a robust caching layer that survives page refreshes
 * and works across browser tabs.
 */

import { get, set, del, clear, keys } from 'idb-keyval';
import { createLogger } from './logger';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  version: string;
}

const CACHE_VERSION = '1.0';
const log = createLogger('persistentCache');

class PersistentCache {
  private memoryCache = new Map<string, CacheEntry<any>>();
  private useIndexedDB = true;

  constructor() {
    this.checkIndexedDBAvailability();
  }

  /**
   * Check if IndexedDB is available
   */
  private async checkIndexedDBAvailability(): Promise<void> {
    try {
      await set('_test_', 'test');
      await del('_test_');
      this.useIndexedDB = true;
      log.info('IndexedDB available for persistent caching');
    } catch (error) {
      log.warn('IndexedDB not available, falling back to memory cache', error);
      this.useIndexedDB = false;
    }
  }

  /**
   * Get data from cache
   */
  async get<T>(key: string): Promise<T | null> {
    const memEntry = this.memoryCache.get(key);
    if (memEntry && this.isValid(memEntry)) {
      log.debug('Memory cache hit', { key });
      return memEntry.data as T;
    }

    if (this.useIndexedDB) {
      try {
        const entry = await get<CacheEntry<T>>(key);
        if (entry && this.isValid(entry)) {
          log.debug('IndexedDB cache hit', { key });
          this.memoryCache.set(key, entry);
          return entry.data;
        } else if (entry) {
          log.debug('Cache entry expired', { key });
          await this.delete(key);
        }
      } catch (error) {
        log.error(`IndexedDB read error for ${key}`, error);
      }
    }

    log.debug('Cache miss', { key });
    return null;
  }

  /**
   * Set data in cache
   */
  async set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): Promise<void> {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      version: CACHE_VERSION,
    };

    this.memoryCache.set(key, entry);

    if (this.useIndexedDB) {
      try {
        await set(key, entry);
        log.debug('Cached entry in IndexedDB', { key, ttl });
      } catch (error) {
        log.error(`IndexedDB write error for ${key}`, error);
      }
    } else {
      log.debug('Cached entry in memory', { key, ttl });
    }
  }

  /**
   * Delete a specific cache entry
   */
  async delete(key: string): Promise<void> {
    this.memoryCache.delete(key);
    if (this.useIndexedDB) {
      try {
        await del(key);
        log.debug('Deleted cache entry', { key });
      } catch (error) {
        log.error(`IndexedDB delete error for ${key}`, error);
      }
    }
  }

  /**
   * Delete entries matching a pattern
   */
  async deletePattern(pattern: RegExp): Promise<void> {
    const memKeys = Array.from(this.memoryCache.keys());
    const matchedMemKeys = memKeys.filter(key => pattern.test(key));
    matchedMemKeys.forEach(key => this.memoryCache.delete(key));

    if (this.useIndexedDB) {
      try {
        const allKeys = await keys();
        const matchedKeys = allKeys.filter(key => pattern.test(String(key)));
        await Promise.all(matchedKeys.map(key => del(key)));
        log.debug('Deleted cache entries by pattern', {
          count: matchedKeys.length,
          pattern: pattern.toString(),
        });
      } catch (error) {
        log.error('IndexedDB pattern delete error', error);
      }
    }
  }

  /**
   * Clear all caches
   */
  async clearAll(): Promise<void> {
    this.memoryCache.clear();
    if (this.useIndexedDB) {
      try {
        await clear();
        log.info('Cleared all persistent caches');
      } catch (error) {
        log.error('IndexedDB clear error', error);
      }
    }
  }

  /**
   * Prune expired entries
   */
  async prune(): Promise<void> {
    let prunedCount = 0;

    // Prune memory cache
    const memEntries = Array.from(this.memoryCache.entries());
    memEntries.forEach(([key, entry]) => {
      if (!this.isValid(entry)) {
        this.memoryCache.delete(key);
        prunedCount++;
      }
    });

    if (this.useIndexedDB) {
      try {
        const allKeys = await keys();
        for (const key of allKeys) {
          const entry = await get<CacheEntry<any>>(String(key));
          if (entry && !this.isValid(entry)) {
            await del(key);
            prunedCount++;
          }
        }
      } catch (error) {
        log.error('IndexedDB prune error', error);
      }
    }

    if (prunedCount > 0) {
      log.info('Pruned expired cache entries', { count: prunedCount });
    }
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    const memSize = this.memoryCache.size;
    let indexedDBSize = 0;

    if (this.useIndexedDB) {
      try {
        const allKeys = await keys();
        indexedDBSize = allKeys.length;
      } catch (error) {
        log.error('IndexedDB stats error', error);
      }
    }

    return {
      memoryEntries: memSize,
      indexedDBEntries: indexedDBSize,
      isIndexedDBAvailable: this.useIndexedDB,
      version: CACHE_VERSION,
    };
  }

  /**
   * Check if cache entry is valid (not expired, correct version)
   */
  private isValid<T>(entry: CacheEntry<T>): boolean {
    if (entry.version !== CACHE_VERSION) {
      return false;
    }
    const age = Date.now() - entry.timestamp;
    return age < entry.ttl;
  }
}

export const persistentCache = new PersistentCache();

setInterval(() => {
  persistentCache.prune();
}, 5 * 60 * 1000);

export const createCacheKey = (...parts: (string | number | boolean | null | undefined)[]): string => {
  return parts.filter(p => p !== null && p !== undefined).join(':');
};

export default persistentCache;
